<template>
  <div>
    <div v-if="license" class="content-block dx-card responsive-paddings">
      <div class="license-content">
        <DxLookup
          :data-source="locationsItems"
          display-expr="name"
          value-expr="code"
          v-model="locationCode"
          :showCancelButton="false"
          labelMode="static"
          :label="$t('license.labelLocation')"
          :placeholder="$t('license.selectLocation')"
          :disabled="isLocationDisabled"
          :dropDownOptions="{ hideOnOutsideClick: true, showTitle: false }"
          @value-changed="onSelectedLocation"
        >
          <DxValidator validation-group="licenseValidationForm">
            <DxRequiredRule :message="$t('general.mandatory')" />
          </DxValidator>
        </DxLookup>

        <div v-if="isSelectedDefaultLocation">
          <fieldset class="activation-procedure">
            <legend>{{ $t('license.activationProcedure') }}</legend>

            <ol>
              <li>{{ $t('license.activationStep1') }}</li>
              <li>{{ $t('license.activationStep2') }}</li>
              <li>{{ $t('license.activationStep3') }}<a href="mailto:support@nicotech.ru">support@nicotech.ru</a>.</li>
              <li>{{ $t('license.activationStep4') }}</li>
            </ol>
          </fieldset>
        </div>

        <DxForm id="form-license"
          v-if="isSelectedLocation"
          labelMode="static"
          :form-data="license"
          :show-colon-after-label=false
          :read-only="isFormDisabled"
          validation-group="licenseValidationForm"
        >
          <DxSimpleItem
            v-if="isSelectedDefaultLocation"
            data-field="location.name"
            :editor-options="{ placeholder: $t('license.selectOrganization') }"
          >
            <DxLabel :text="$t('license.descriptionLabel')" />
            <DxRequiredRule :message="$t('general.mandatory')" />
          </DxSimpleItem>

          <DxGroupItem :caption="$t('license.captionLicenseDate')" :col-count="2" >
            <DxSimpleItem
              data-field="dateFrom"
              editor-type="dxDateBox"
              :editor-options=editorsOptions.calendarOptions
            >
              <DxLabel :text="$t('license.from')" />
              <DxRequiredRule :message="$t('general.mandatory')" />
            </DxSimpleItem>

            <DxSimpleItem
              data-field="dateTo"
              editor-type="dxDateBox"
              :editor-options=editorsOptions.calendarOptions
            >
              <DxLabel :text="$t('license.to')" />
              <DxRequiredRule :message="$t('general.mandatory')" />
            </DxSimpleItem>
          </DxGroupItem>

          <DxGroupItem :caption="$t('license.devices')" :col-count="3">
            <DxSimpleItem data-field="deviceCount" editor-type="dxNumberBox" :editor-options=editorsOptions.deviceCount>
              <DxLabel :text="$t('license.available')" />
              <DxRequiredRule :message="$t('general.mandatory')" />
            </DxSimpleItem>

            <DxSimpleItem data-field="activeDeviceCount" editor-type="dxNumberBox" :editor-options=editorsOptions.activeDeviceCount>
              <DxLabel :text="$t('license.serviced')" />
            </DxSimpleItem>

            <DxSimpleItem data-field="dbDeviceCount" editor-type="dxNumberBox" :editor-options=editorsOptions.dbDeviceCount>
              <DxLabel :text="$t('license.detected')" />
            </DxSimpleItem>
          </DxGroupItem>
        </DxForm>

        <div v-if="isSelectedDefaultLocation" class="action-btn">
          <active-button
            type="success"
            :style="{padding: '0px 10px'}"
            :text="$t('license.buttons.buttonRequest')"
            @click="requestLicense"
          />
          <DxFileUploader
            :element-attr="{class: 'upload'}"
            :labelText="$t('license.buttons.uploaderDropButtonText')"
            :uploadedMessage="`${isUploadFile ? $t('general.UploadedsuccessfullyLabel') : $t('general.UploadedfailedLabel')}`"
            :allowed-file-extensions=allowedFileExtensionsUpload
            accept=".lic"
            uploadMode="useForm"
            @value-changed=onUploaded
          />
        </div>
      </div>

      <dx-load-panel :visible="loading" :position="{ of: '.license-content' }" />

      <div
        v-if="!isSelectedDefaultLocation && isDefaultAdmin"
        class="app-form-buttons crud"
      >
        <aims2-button
          icon="save"
          type="default"
          :text="$t('user.actions.save')"
          :disabled="isSaveDisabled"
          :busy="saving"
          @click="onSave"
        />

        <aims2-button
          icon="trash"
          type="danger"
          :text="$t('user.actions.delete')"
          :disabled="isDeleteDisabled"
          :busy="deleting"
          @click="onDelete"
        />
      </div>
    </div>
  </div>
</template>

<script>
import baseView from '../base.vue';
import appInfo from '../../app-info';

import { mapActions, mapGetters } from 'vuex'

import {
  DxForm,
  DxGroupItem,
  DxSimpleItem,
  DxRequiredRule,
  DxLabel
} from 'devextreme-vue/form';
import { DxLookup } from 'devextreme-vue/lookup';
import { DxLoadPanel } from "devextreme-vue/load-panel";
import { DxValidator } from 'devextreme-vue/validator';
import ValidationEngine from 'devextreme/ui/validation_engine';

import { notifier } from '@/shared/lib/notifier/index';

import ActiveButton from '@/components/active-button.vue';
import { DxFileUploader } from 'devextreme-vue/file-uploader';
import aims2Button from "@/components/active-button.vue";
import showConfirmDialog from "@/components/confirm-dialog";

import { AclHandler } from "@/services/acl-handler";
import { postForm } from '@/services/base';
import {
  userLocationsManagedLicense, getLocationDevices, download, postLicenses,
  putLicenses, deleteLicenses, getUserLicenses, getLicense
} from "@/services/api";

import { isFinite } from 'lodash';

export default {
  name: 'license',
  extends: baseView,
  mixins: [],
  components: {
    aims2Button, DxLookup, DxForm, DxGroupItem, DxSimpleItem, DxRequiredRule, DxLabel, DxFileUploader,
    ActiveButton, DxValidator, DxLoadPanel
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      license: {
        location: null,
        dateFrom: new Date(),
        dateTo: new Date(Date.now() + 1000 * 60 * 60 * 24 * 14),
        deviceCount: null,
        activeDeviceCount: null,
        dbDeviceCount: null,
      },
      locationsItems: [],
      locationCode: null,
      selectedLocation: null,
      loading: false,
      saving: false,
      deleting: false,
      isUploadFile: true,
      allowedFileExtensionsUpload: ['.lic'],
    };
  },
  computed: {
    ...mapGetters([
      'getLocations'
    ]),
    editorsOptions() {
      return {
        calendarOptions: {
          displayFormat: 'dd.MM.yyyy',
          disabled: this.isSelectedDefaultLocation
        },
        deviceCount: {
          min: 1,
          format: '#0',
          disabled: this.isSelectedDefaultLocation
        },
        activeDeviceCount: { disabled: true },
        dbDeviceCount: { disabled: true }
      }
    },
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    isDefaultAdmin() {
      return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] }) && this.aclUser.hasDefaultLocation();
    },
    //
    // {object} route.params
    // - id {number} индификатор лицензии для получения данных
    // - id {string} код локации для создания новой лицензии
    //
    licenseId() {
      if (this.$route.params.id === 'DEFAULT') {
        return -2;
      } else {
        const id = Number(this.$route.params.id);
        return isFinite(id) ? id : -1;
      }
    },
    isNew() {
      return this.licenseId < 0;
    },
    isSelectedLocation() {
      return this.selectedLocation ? true : false;
    },
    isSelectedDefaultLocation() {
      return this.locationCode === 'DEFAULT';
    },
    isLocationDisabled() {
      return !this.isNew || !this.isDefaultAdmin || this.isSelectedDefaultLocation || this.saving || this.deleting || this.loading;
    },
    isFormDisabled() {
      return !this.isDefaultAdmin || this.saving || this.deleting;
    },
    isSaveDisabled() {
      return this.loading || this.isSelectedDefaultLocation || this.saving || this.deleting;
    },
    isDeleteDisabled() {
      return this.loading || this.deleting || this.saving || this.isNew;
    }
  },
  methods: {
    ...mapActions([
      'setLocations'
    ]),
    validate() {
      return ValidationEngine.validateGroup("licenseValidationForm");
    },
    async onSelectedLocation(e) {
      this.selectedLocation = this.locationsItems.find(l => l.code === e.value);

      if (!this.isSelectedDefaultLocation && this.selectedLocation) {
        getLocationDevices(this.selectedLocation.id).then(res => {
          this.license.activeDeviceCount = res.activeDeviceCount;
          this.license.dbDeviceCount = res.allDeviceCount;
        });
      }
    },
    async requestLicense() {
      const validate = this.validate();
      if (!validate.isValid) {
        validate.brokenRules[0].validator.focus();  
        return;
      }

      try {
          await download(`/instance-license/request/?companyName=${this.license.location.name}`, 'lic-request.json');
          notifier.showSuccess(this.$t('general.uploadSuccess'));
      } catch (error) {
          this.onDataError(error);
      }
    },
    async onUploaded(e) {
      if (e.value.length !== 0) {
          const formData = new FormData();
          formData.append("licenseFile", e.value[0]);
          try {
              this.isUploadFile = true;
              await postForm('/instance-license', formData);
              notifier.showSuccess(this.$t('general.uploadSuccess'));
          } catch (error) {
              this.onDataError(error);
              this.isUploadFile = false;
          }
      }
    },
    async onSave() {
      const validate = this.validate();
      if (!validate.isValid) {
        validate.brokenRules[0].validator.focus();  
        return;
      }

      try {
        this.saving = true;

        const payload = {
          dateFrom: this.license.dateFrom,
          dateTo: this.license.dateTo,
          deviceCount: this.license.deviceCount,
          location: this.locationsItems.find(l => l.code === this.locationCode)
        };

        if(!this.isNew) {
          await putLicenses(this.license.id, payload);
        } else {
          await postLicenses(payload);
        }

        this.$router.go(-1);
      } catch(err) {
        this.onDataError(err);
      } finally {
        this.saving = false;
      }
    },
    async onDelete() {
      const response = await showConfirmDialog({
        title: this.$t('license.deletetitle'),
        message: this.$t('license.deleteDescription'),
        btnType: "danger",
        okButton: this.$t('general.proceed'),
        cancelButton: this.$t('general.cancel'),
      });

      if (response) {
        this.deleting = true;
        try {
          await deleteLicenses(this.license.id);
          notifier.showSuccess(this.$t('license.licenseDeleted'));
          this.$router.go(-1);
        } catch (err) {
          this.onDataError(err);
        } finally {
          this.deleting = false;
        }
      }
    },
    async load() {
      try {
        this.loading = true;

        this.locationsItems = await userLocationsManagedLicense();
        await this.setLocations();
      } catch (error) {
        this.onDataError(error);
      } finally {
        if (!this.isNew) {
          const license = await getUserLicenses(this.licenseId);

          if (!this.locationsItems.some(l => l.code === license.location.code))
            this.locationsItems.push(license.location);

          this.locationCode = license.location.code;

          this.license = license;
        } else {
          if (this.licenseId === -2) {
            const defaultLicense = await getLicense();
            const defaultLocation = this.$store.getters.defaultLocation;
            this.locationCode = defaultLocation.code;
            this.locationsItems.push(defaultLocation);

            if (defaultLicense.status !== 'ERROR') {
              this.license.location = defaultLocation;
              this.license.dateFrom = defaultLicense.from;
              this.license.dateTo = defaultLicense.to;
              this.license.deviceCount = defaultLicense.deviceCount;
              this.license.activeDeviceCount = defaultLicense.activeDeviceCount;
              this.license.dbDeviceCount = defaultLicense.dbDeviceCount;
            }
          } else {
            const id = this.$route.params.id;
            if (!isFinite(id)) {
              this.locationCode = this.$route.params.id; // код локации для создания новой лицензии

              const location = this.getLocations.find(l => l.code === this.locationCode);

              if (!this.locationsItems.some(l => l.code === this.locationCode))
                this.locationsItems.push(location);
            }
          }
        }

        this.loading = false;
      }
    }
  },
  async created() {
    document.title = `${appInfo.title} - ${this.$t('licenses.title')}`;

    await this.load();

    this.$store.commit("setBreadcrumb", [{
      link: "/licenses",
      title: `${this.$t('menu.licenses')}`
    }, {
      link: this.licenseId ? `/license/${this.licenseId}`: "/licenses",
      title: this.license.location ? this.license.location.name : `${this.$t('license.title')}`
    }]);
  },
  watch: {  }
};
</script>
  
<style lang="scss">
.license-content {
  width: 600px;

  fieldset {
    margin: 0;
    border-radius: 4px;
    border: 1px solid #ddd;

    legend {
      font-weight: 500;
    }
  }

  .activation-procedure {
    background-color: rgb(255, 252, 229);
    margin-bottom: 10px;

    ol {
      padding-left: 20px;
      margin: 0;
    }
  }

  .action-btn {
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 5px;

    .upload {
        flex: 2;
    }
  }

  #form-license {
    margin-top: 10px;

    .dx-item {
      margin-bottom: 0;
    }
    .dx-form-group-with-caption > .dx-form-group-content {
      padding-bottom: 0;
    }
  }
}

.crud {
  margin-top: 30px;
}
</style>  